import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Avatar,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageProvider';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ShareIcon from '@mui/icons-material/Share';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';

export const OurBlogs = () => {
  const { currentLang, direction } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate(); // 👈 أضف هذا

  // دوال مساعدة للـ Avatar
  const getInitials = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // خذ الحرف الأول من الاسم الأول فقط
      return nameParts[0][0].toUpperCase();
    }
    // لو اسم واحد، خذ أول حرف فقط
    return name.substring(0, 1).toUpperCase();
  };

  const maskSecondName = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // الاسم الأول كامل + الحرف الأول من الاسم الثاني + نقاط
      const firstName = nameParts[0];
      const secondNameFirstLetter = nameParts[1][0];
      return `${firstName} ${secondNameFirstLetter}.`;
    }
    return name;
  };

  // لون موحد للـ Avatar - أزرق ثابت
  const AVATAR_COLOR = '#4361ee';

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Blog data
  const blogsData = {
    en: [
      {
        id: 1,
        title: 'Understanding UAE Labor Law: A Comprehensive Guide',
        excerpt: 'Learn about the key aspects of UAE labor law, including employee rights, contract types, and termination procedures.',
        content: `The UAE Labor Law is designed to protect both employers and employees, ensuring a fair and productive working environment. This comprehensive guide covers:

        1. Employment Contracts: Fixed-term and unlimited contracts explained
        2. Working Hours: Standard hours, overtime, and flexible arrangements
        3. Leave Entitlements: Annual, sick, and special leave provisions
        4. Termination: Legal procedures and end-of-service benefits
        5. Dispute Resolution: How to handle workplace conflicts
        
        Understanding these regulations is crucial for both employers and employees to maintain compliance and avoid legal issues.`,
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Labor Law',
        date: '2024-03-15',
        readTime: '8 min',
        author: 'Ahmed Al Mansoori',
        views: 1234,
        icon: GavelIcon
      },
      {
        id: 2,
        title: 'Starting a Business in UAE: Legal Requirements',
        excerpt: 'A step-by-step guide to understanding the legal framework for setting up a business in UAE and the UAE.',
        content: `UAE offers tremendous opportunities for entrepreneurs, but navigating the legal requirements can be complex. This guide walks you through:

        1. Business Structures: Mainland vs. Free Zone vs. Offshore
        2. Licensing Requirements: Commercial, industrial, and professional licenses
        3. Visa Processes: Investor and employment visas
        4. Local Sponsorship: When and how it's required
        5. Compliance: Ongoing legal obligations
        
        Whether you're a local entrepreneur or foreign investor, understanding these requirements is essential for success.`,
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Business Setup',
        date: '2024-03-10',
        readTime: '10 min',
        author: 'Sara Al Hashimi',
        views: 2156,
        icon: BusinessIcon
      },
      {
        id: 3,
        title: 'UAE Rental Disputes: What Tenants and Landlords Need to Know',
        excerpt: 'Essential information about resolving rental disputes in UAE through the Rental Dispute Settlement Center.',
        content: `Rental disputes are common in UAE's dynamic real estate market. This article explains:

        1. RERA and Ejari: Registration requirements and importance
        2. Rent Increases: Legal limits and notification periods
        3. Maintenance Responsibilities: Who pays for what
        4. Eviction Rules: Legal grounds and procedures
        5. Dispute Resolution: How to file a case at the Rental Dispute Center
        
        Both tenants and landlords benefit from understanding their rights and obligations under UAE's rental laws.`,
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Property Law',
        date: '2024-03-05',
        readTime: '7 min',
        author: 'Mohammed Al Falasi',
        views: 1876,
        icon: AccountBalanceIcon
      },
      {
        id: 4,
        title: 'Legal Tips for Digital Nomads in the UAE',
        excerpt: 'Important legal considerations for remote workers and digital nomads planning to relocate to the UAE.',
        content: `The UAE has become a hotspot for digital nomads. Here's what you need to know legally:

        1. Visa Options: Remote work visas and freelance permits
        2. Tax Implications: Understanding your tax obligations
        3. Contract Considerations: Service agreements and NDAs
        4. Intellectual Property: Protecting your digital assets
        5. Banking and Finance: Setting up accounts and transferring money
        
        Stay compliant while enjoying the benefits of working from this dynamic country.`,
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Digital Nomads',
        date: '2024-02-28',
        readTime: '6 min',
        author: 'Fatima Al Zaabi',
        views: 1432,
        icon: DescriptionIcon
      }
    ],
    ar: [
      {
        id: 1,
        title: 'دليل شامل لقانون العمل الإماراتي',
        excerpt: 'تعرف على الجوانب الرئيسية لقانون العمل الإماراتي، بما في ذلك حقوق الموظفين وأنواع العقود وإجراءات إنهاء الخدمة.',
        content: `صُمم قانون العمل الإماراتي لحماية كل من أصحاب العمل والموظفين، مما يضمن بيئة عمل عادلة ومنتجة. يغطي هذا الدليل الشامل:

        1. عقود العمل: شرح العقود محددة المدة وغير المحددة
        2. ساعات العمل: الساعات القياسية والعمل الإضافي والترتيبات المرنة
        3. استحقاقات الإجازات: الإجازات السنوية والمرضية والخاصة
        4. إنهاء الخدمة: الإجراءات القانونية ومكافآت نهاية الخدمة
        5. تسوية المنازعات: كيفية التعامل مع النزاعات في مكان العمل
        
        فهم هذه اللوائح أمر بالغ الأهمية لكل من أصحاب العمل والموظفين للحفاظ على الامتثال وتجنب المشكلات القانونية.`,
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العمل',
        date: '2024-03-15',
        readTime: '8 دقائق',
        author: 'أحمد المنصوري',
        views: 1234,
        icon: GavelIcon
      },
      {
        id: 2,
        title: 'تأسيس شركة في دبي: المتطلبات القانونية',
        excerpt: 'دليل خطوة بخطوة لفهم الإطار القانوني لتأسيس شركة في دبي والإمارات.',
        content: `تقدم دبي فرصاً هائلة لرواد الأعمال، لكن فهم المتطلبات القانونية قد يكون معقداً. يرشدك هذا الدليل خلال:

        1. هياكل الأعمال: المناطق الحرة vs. البر الرئيسي vs. المناطق الحرة
        2. متطلبات الترخيص: التراخيص التجارية والصناعية والمهنية
        3. إجراءات التأشيرات: تأشيرات المستثمرين وتأشيرات العمل
        4. الكفالة المحلية: متى وكيف تكون مطلوبة
        5. الامتثال: الالتزامات القانونية المستمرة
        
        سواء كنت رائد أعمال محلياً أو مستثمراً أجنبياً، فهم هذه المتطلبات ضروري للنجاح.`,
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'تأسيس الأعمال',
        date: '2024-03-10',
        readTime: '10 دقائق',
        author: 'سارة الهاشمي',
        views: 2156,
        icon: BusinessIcon
      },
      {
        id: 3,
        title: 'منازعات الإيجار في دبي: ما يحتاج المستأجرون والمالكون معرفته',
        excerpt: 'معلومات أساسية حول حل المنازعات الإيجارية في دبي من خلال مركز فض المنازعات الإيجارية.',
        content: `المنازعات الإيجارية شائعة في سوق العقارات الديناميكي في دبي. يشرح هذا المقال:

        1. مؤسسة التنظيم العقاري وإيجاري: متطلبات التسجيل وأهميتها
        2. زيادات الإيجار: الحدود القانونية وفترات الإخطار
        3. مسؤوليات الصيانة: من يدفع ماذا
        4. قواعد الإخلاء: الأسباب والإجراءات القانونية
        5. تسوية المنازعات: كيفية رفع قضية في مركز المنازعات الإيجارية
        
        يستفيد كل من المستأجرين والمالكين من فهم حقوقهم والتزاماتهم بموجب قوانين الإيجار في دبي.`,
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العقارات',
        date: '2024-03-05',
        readTime: '7 دقائق',
        author: 'محمد الفلاسي',
        views: 1876,
        icon: AccountBalanceIcon
      },
      {
        id: 4,
        title: 'نصائح قانونية للرحالة الرقميين في الإمارات',
        excerpt: 'اعتبارات قانونية مهمة للعاملين عن بعد والرحالة الرقميين الذين يخططون للانتقال إلى الإمارات.',
        content: `أصبحت الإمارات وجهة مفضلة للرحالة الرقميين. إليك ما تحتاج معرفته قانونياً:

        1. خيارات التأشيرة: تأشيرات العمل عن بعد وتصاريح العمل الحر
        2. الآثار الضريبية: فهم التزاماتك الضريبية
        3. اعتبارات العقود: اتفاقيات الخدمة واتفاقيات عدم الإفصاح
        4. الملكية الفكرية: حماية أصولك الرقمية
        5. الخدمات المصرفية والمالية: فتح حسابات وتحويل الأموال
        
        حافظ على الامتثال القانوني أثناء الاستمتاع بفوائد العمل من هذه الدولة الديناميكية.`,
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'الرحالة الرقميون',
        date: '2024-02-28',
        readTime: '6 دقائق',
        author: 'فاطمة الزعابي',
        views: 1432,
        icon: DescriptionIcon
      }
    ]
  };

  const currentBlogs = blogsData[currentLang];

  const handleOpenBlog = (blog) => {
    setSelectedBlog(blog);
    setOpenDialog(true);
  };

  const handleCloseBlog = () => {
    setOpenDialog(false);
    setTimeout(() => setSelectedBlog(null), 300);
  };

  return (
    <Box
      id="blogs"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 45, 0],
          opacity: [0.1, 0.15, 0.1]
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          position: 'absolute',
          top: '5%',
          right: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(102,126,234,0.1) 0%, rgba(102,126,234,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <motion.div
        animate={floatingAnimation}
        style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(254,107,139,0.1) 0%, rgba(254,107,139,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section Title */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 'bold',
                  color: '#1a1a2e',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                {currentLang === 'ar' ? 'المدونة القانونية' : 'Legal Blog'}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  color: '#667eea',
                  fontWeight: 500,
                  mb: 3,
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                {currentLang === 'ar'
                  ? 'أحدث المقالات والأخبار القانونية في الإمارات'
                  : 'Latest legal articles and news in the UAE'}
              </Typography>
              <Divider sx={{
                width: '80px',
                margin: '0 auto',
                height: '4px',
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
                borderRadius: '2px'
              }} />
            </Box>
          </motion.div>

          {/* Blog Cards Grid */}
          <Grid container spacing={4}>
            {currentBlogs.map((blog, index) => {
              const Icon = blog.icon;
              return (
                <Grid item xs={12} md={6} key={blog.id}>
                  <motion.div
                    variants={itemVariants}
                    whileHover={{
                      y: -15,
                      scale: 1.02,
                      transition: { type: "spring", stiffness: 300 }
                    }}
                    style={{ height: '100%' }}
                  >
                    <Card
                      onClick={() => handleOpenBlog(blog)}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 20px 40px rgba(102,126,234,0.2)'
                        }
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="240"
                        image={blog.image}
                        alt={blog.title}
                        sx={{
                          transition: 'transform 0.5s ease',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                      
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        {/* Category Chip */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Chip
                            icon={<Icon />}
                            label={blog.category}
                            size="small"
                            sx={{
                              bgcolor: '#667eea',
                              color: 'white',
                              fontWeight: 500,
                              '& .MuiChip-icon': {
                                color: 'white'
                              }
                            }}
                          />
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: '#999' }} />
                            <Typography variant="caption" sx={{ color: '#999' }}>
                              {blog.readTime}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Title */}
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            fontWeight: 'bold',
                            color: '#1a1a2e',
                            mb: 2,
                            lineHeight: 1.4,
                            minHeight: '3.5rem'
                          }}
                        >
                          {blog.title}
                        </Typography>

                        {/* Excerpt */}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            color: '#666',
                            lineHeight: 1.8,
                            mb: 3
                          }}
                        >
                          {blog.excerpt}
                        </Typography>

                        {/* Author and Date - مع Avatar نصي */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: AVATAR_COLOR,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.9rem'
                              }}
                            >
                              {getInitials(blog.author)}
                            </Avatar>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {maskSecondName(blog.author)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 14, color: '#999' }} />
                              <Typography variant="caption" sx={{ color: '#999' }}>
                                {blog.date}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <RemoveRedEyeIcon sx={{ fontSize: 14, color: '#999' }} />
                              <Typography variant="caption" sx={{ color: '#999' }}>
                                {blog.views}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                      
                      <CardActions sx={{ p: 3, pt: 0 }}>
                        <Button
                          size="small"
                          sx={{
                            color: '#667eea',
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                            fontWeight: 600,
                            '&:hover': {
                              bgcolor: 'transparent',
                              color: '#764ba2'
                            }
                          }}
                        >
                          {currentLang === 'ar' ? 'اقرأ المزيد' : 'Read More'}
                        </Button>
                      </CardActions>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>

          {/* View All Button */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/blogs')} // 👈 هنا
          sx={{
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            color: 'white',
            px: 6,
            py: 1.5,
            fontSize: '1.1rem',
            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
            fontWeight: 600,
            borderRadius: '50px',
            boxShadow: '0 8px 20px rgba(102,126,234,0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)',
              boxShadow: '0 12px 25px rgba(102,126,234,0.4)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          {currentLang === 'ar' ? 'عرض جميع المقالات' : 'View All Articles'}
        </Button>
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      {/* Blog Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseBlog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'white',
            maxHeight: '90vh'
          }
        }}
      >
        {selectedBlog && (
          <>
            <DialogTitle sx={{
              p: 0,
              position: 'relative'
            }}>
              <Box
                sx={{
                  height: '300px',
                  backgroundImage: `url(${selectedBlog.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)'
                  }}
                />
                
                <IconButton
                  onClick={handleCloseBlog}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: direction === 'rtl' ? 'auto' : 16,
                    left: direction === 'rtl' ? 16 : 'auto',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)'
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    left: 20,
                    right: 20,
                    color: 'white'
                  }}
                >
                  <Chip
                    icon={React.createElement(selectedBlog.icon)}
                    label={selectedBlog.category}
                    sx={{
                      bgcolor: '#667eea',
                      color: 'white',
                      mb: 2,
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                      fontWeight: 'bold',
                      mb: 1
                    }}
                  >
                    {selectedBlog.title}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2">
                        {maskSecondName(selectedBlog.author)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2">
                        {selectedBlog.date}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2">
                        {selectedBlog.readTime}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2">
                        {selectedBlog.views} {currentLang === 'ar' ? 'مشاهدة' : 'views'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  color: '#1a1a2e',
                  lineHeight: 1.8,
                  whiteSpace: 'pre-line'
                }}
              >
                {selectedBlog.content}
              </Typography>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'space-between' }}>
              <Button
                startIcon={<ShareIcon />}
                sx={{
                  color: '#667eea',
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter'
                }}
              >
                {currentLang === 'ar' ? 'مشاركة' : 'Share'}
              </Button>
              <Button
                variant="contained"
                onClick={handleCloseBlog}
                sx={{
                  bgcolor: '#667eea',
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  '&:hover': {
                    bgcolor: '#764ba2'
                  }
                }}
              >
                {currentLang === 'ar' ? 'إغلاق' : 'Close'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OurBlogs;